import { useCallback, useState } from 'react';
import {
  BlacklistUploadVariant,
  CampaignBuilderSteps,
  EnrichmentVariants,
  FlowCreationTabs,
  QualificationPromptPlaceholders,
  Qualifications,
  StartNewOrClone,
} from 'features/co-pilot/campaign-builder/campaign-builder.types';
import {
  AddContact,
  FileUpload,
  FunnelContact,
  RecruiterContact,
  RecruiterSearch,
  Search,
} from 'features/co-pilot/co-pilot.types';
import { Blacklist_New, BlacklistTypes } from 'features/co-pilot/company-blacklists/company-blacklist.types';
import { FaqItemKey } from 'features/onboarding/faq/faq-dialog.types';

/**
 * Comprehensive hook for all dialog states in campaign-co-pilot.tsx
 * This consolidates ~600 lines of useState declarations into a single hook
 */

// ============ Initial States ============

const initialRecruiterSearchConstraints: RecruiterSearch = {
  occupation: { query: '', options: [], selected: [], titleType: undefined, loading: false },
  skill: { query: '', options: [], selected: [], loading: false },
  industry: { query: '', options: [], selected: [], loading: false },
  company: { query: '', options: [], selected: [], loading: false },
  geo: { query: '', options: [], selected: [], loading: false },
  zip: { query: '', options: [], selected: [], loading: false },
  area: '25',
  keywords: '',
  yearsOfExperience: [0, 30],
};

const initialQualificationPromptPlaceholders: QualificationPromptPlaceholders = {
  required: '',
  disqualifying: '',
  preferred: '',
  additionalContactInfo: '',
  companyCharacteristics: '',
  companyChallenges: '',
  organizationalWorkingCultureValues: '',
};

// ============ Hook ============

export function useCampaignCoPilotDialogs() {
  // Campaign creation dialogs
  const [createCampaignDialog, setCreateCampaignDialog] = useState<{
    open: boolean;
    loading: boolean;
    nameTakenError: boolean;
    name: string;
    selectedLinkedinAccount: string;
    startNewOrClone: StartNewOrClone;
    selectedCampaignToClone: string;
    cloneContacts: boolean;
  }>({
    open: false,
    loading: false,
    nameTakenError: false,
    name: '',
    selectedLinkedinAccount: '',
    startNewOrClone: StartNewOrClone.START_NEW,
    selectedCampaignToClone: '',
    cloneContacts: false,
  });
  const [createCampaignSuccessDialog, setCreateCampaignSuccessDialog] = useState<{ open: boolean }>({ open: false });
  const [openTemplateSuccessDialog, setOpenTemplateSuccessDialog] = useState(false);

  // Search dialogs
  const [refineLinkedinSearchDialog, setRefineLinkedinSearchDialog] = useState<{
    open: boolean;
    loading: boolean;
    originalLinkedinUrl: string;
    alteredLinkedinUrl: string;
    urlError: boolean;
  }>({ open: false, loading: false, originalLinkedinUrl: '', alteredLinkedinUrl: '', urlError: false });

  const [refineRecruiterSearchDialog, setRefineRecruiterSearchDialog] = useState<{
    open: boolean;
    loading: boolean;
    crawlerLinkedinAccount: string | null;
    preLoadingFields: boolean;
    contactsLoading: boolean;
    constraints: RecruiterSearch;
    contacts: RecruiterContact[];
    totalContacts: number;
    pageNumber: number;
    searchRequestId: string | undefined;
    searchHistoryId: string | undefined;
    searchContextId: string | undefined;
  }>({
    open: false,
    loading: false,
    crawlerLinkedinAccount: null,
    preLoadingFields: false,
    contactsLoading: false,
    constraints: initialRecruiterSearchConstraints,
    contacts: [],
    totalContacts: 0,
    pageNumber: 1,
    searchRequestId: undefined,
    searchHistoryId: undefined,
    searchContextId: undefined,
  });

  const [createNewSearchDialog, setCreateNewSearchDialog] = useState<{
    open: boolean;
    loading: boolean;
    searchName: string;
    numberOfContactsToLoad: number;
  }>({
    open: false,
    loading: false,
    searchName: '',
    numberOfContactsToLoad: 50,
  });

  const [addMoreSearchesDialog, setAddMoreSearchesDialog] = useState<{ open: boolean; searchName: string }>({ open: false, searchName: '' });

  // Qualification dialogs
  const [qualifyContactsDialog, setQualifyContactsDialog] = useState<{
    open: boolean;
    loading: boolean;
    step: number;
    promptPlaceholders: QualificationPromptPlaceholders;
    selectedOptions: string[];
    prompt: string;
  }>({
    open: false,
    loading: false,
    step: 0,
    promptPlaceholders: initialQualificationPromptPlaceholders,
    selectedOptions: [Qualifications.A, Qualifications.B],
    prompt: '',
  });

  // Contact management dialogs
  const [loadContactsNumberDialog, setLoadContactsNumberDialog] = useState<{
    open: boolean;
    loading: boolean;
    search: Search | null;
    numberOfContactsToLoad: number;
  }>({
    open: false,
    loading: false,
    search: null,
    numberOfContactsToLoad: 50,
  });

  const [fileUploadDialog, setFileUploadDialog] = useState<{
    open: boolean;
    columns: { text: string; type: string }[];
    data: string[][];
    fileNames: string[];
    hasTableHeading: boolean;
  }>({
    open: false,
    columns: [],
    data: [],
    fileNames: [],
    hasTableHeading: true,
  });

  const [renameContactSearchDialog, setRenameContactSearchDialog] = useState<{
    open: boolean;
    loading: boolean;
    search: Search | null;
    name: string;
  }>({ open: false, loading: false, search: null, name: '' });

  const [editSearchLinkedinAccountDialog, setEditSearchLinkedinAccountDialog] = useState<{
    open: boolean;
    loading: boolean;
    search: Search | null;
    selectedLinkedinAccount: string;
  }>({ open: false, loading: false, search: null, selectedLinkedinAccount: '' });

  const [deleteContactsSearchDialog, setDeleteContactsSearchDialog] = useState<{
    open: boolean;
    loading: boolean;
    search: Search | undefined;
    deleteContacts: boolean;
  }>({
    open: false,
    loading: false,
    search: undefined,
    deleteContacts: false,
  });

  const [deleteContactsFileDialog, setDeleteContactsFileDialog] = useState<{
    open: boolean;
    loading: boolean;
    file: FileUpload | null;
  }>({ open: false, loading: false, file: null });

  const [addSingleContactDialog, setAddSingleContactDialog] = useState<{
    open: boolean;
    loading: boolean;
    contact: AddContact;
  }>({ open: false, loading: false, contact: { linkedinUrl: '', firstName: '', lastName: '', email: '', company: '', phoneNumber: '' } });

  const [deleteContactPermanentlyDialog, setDeleteContactPermanentlyDialog] = useState<{
    open: boolean;
    loading: boolean;
    contact: FunnelContact | null;
  }>({ open: false, loading: false, contact: null });

  const [changeAiJudgementDialog, setChangeAiJudgementDialog] = useState<{
    open: boolean;
    loading: boolean;
    contact: FunnelContact | null;
    newJudgement: string;
  }>({
    open: false,
    loading: false,
    contact: null,
    newJudgement: '',
  });

  const [deleteMultipleContactsPermanentlyDialog, setDeleteMultipleContactsPermanentlyDialog] = useState<{
    open: boolean;
    loading: boolean;
    contacts: FunnelContact[];
    contactCount: number;
  }>({
    open: false,
    loading: false,
    contacts: [],
    contactCount: 0,
  });

  // Blacklist dialogs
  const [addLocalBlacklistDialog, setAddLocalBlacklistDialog] = useState<{
    open: boolean;
    loading: boolean;
    blacklist?: Blacklist_New | null;
    blacklistName: string;
    blacklistType: BlacklistTypes;
    blacklistUploadVariant: BlacklistUploadVariant;
    individualItemsTextField: string;
    individualItemsTextFieldError: true | null;
    individualItemsList: string[];
    fileColumnSelection: {
      columns: { text: string; type: string }[];
      data: string[][];
      fileNames: string[];
      hasTableHeading: boolean;
    };
    fileItemsList: { url?: string; company?: string }[];
    selectedLinkedinAccount: string;
    companyQuery: string;
    companyOptions: string[][];
    selectedCompanies: string[][];
    companyLoading: boolean;
    companyIsEmpty: boolean;
  }>({
    open: false,
    loading: false,
    blacklist: null,
    blacklistName: '',
    blacklistType: BlacklistTypes.CONTACT,
    blacklistUploadVariant: BlacklistUploadVariant.FILE,
    individualItemsTextField: '',
    individualItemsTextFieldError: null,
    individualItemsList: [],
    fileColumnSelection: {
      columns: [],
      data: [],
      fileNames: [],
      hasTableHeading: true,
    },
    fileItemsList: [],
    selectedLinkedinAccount: '',
    companyQuery: '',
    companyOptions: [],
    selectedCompanies: [],
    companyLoading: false,
    companyIsEmpty: false,
  });

  const [deleteBlacklistDialog, setDeleteBlacklistDialog] = useState<{ open: boolean; loading: boolean; blacklist: Blacklist_New | null }>({
    open: false,
    loading: false,
    blacklist: null,
  });

  const [editBlacklistNameDialog, setEditBlacklistNameDialog] = useState<{
    open: boolean;
    loading: boolean;
    blacklist: Blacklist_New | null;
    blacklistName: string;
  }>({
    open: false,
    loading: false,
    blacklist: null,
    blacklistName: '',
  });

  // Enrichment dialogs
  const [enrichContactsDialog, setEnrichContactsDialog] = useState<{
    open: boolean;
    loading: boolean;
    apiKey: string;
    variant: EnrichmentVariants;
    prospeosEnrichEmail: boolean;
    prospeosEnrichPhone: boolean;
    aiResearchFullFunnel: boolean;
    customFieldPersonPrompt: string;
    customFieldCompanyPrompt: string;
    customFieldOverwriteExisting: boolean;
  }>({
    open: false,
    loading: false,
    apiKey: '',
    variant: EnrichmentVariants.SHORT_LIST,
    prospeosEnrichEmail: false,
    prospeosEnrichPhone: false,
    aiResearchFullFunnel: false,
    customFieldPersonPrompt: '',
    customFieldCompanyPrompt: '',
    customFieldOverwriteExisting: false,
  });

  const [enrichmentLoadingDialog, setEnrichmentLoadingDialog] = useState<{ open: boolean }>({ open: false });

  // Flow creation dialogs
  const [flowCreationDialog, setFlowCreationDialog] = useState<{ open: boolean; loading: boolean; activeTab: FlowCreationTabs }>({
    open: false,
    loading: false,
    activeTab: FlowCreationTabs.FROM_TEMPLATE,
  });

  const [changeCommunicationStyleWarningDialog, setChangeCommunicationStyleWarningDialog] = useState<{ open: boolean; loading: boolean }>({
    open: false,
    loading: false,
  });

  // Campaign start/stop dialogs
  const [startCampaignSuccessDialog, setStartCampaignSuccessDialog] = useState<{ open: boolean }>({ open: false });

  const [startMessageApprovalCampaignSuccessDialog, setStartMessageApprovalCampaignSuccessDialog] = useState<{
    open: boolean;
    campaign: any | null;
    linkedinAccount: any | null;
  }>({ open: false, campaign: null, linkedinAccount: null });

  const [startImmediatelyDialog, setStartImmediatelyDialog] = useState<{ open: boolean; loading: boolean }>({ open: false, loading: false });
  const [startImmediatelySuccessDialog, setStartImmediatelySuccessDialog] = useState<{ open: boolean }>({ open: false });
  const [flowIncompleteDialog, setFlowIncompleteDialog] = useState({ open: false });
  const [flowEmptyDialog, setFlowEmptyDialog] = useState({ open: false });

  const [creditsExhaustedDialog, setCreditsExhaustedDialog] = useState<{ open: boolean }>({ open: false });
  const [copilotLicenseExpiredDialog, setCopilotLicenseExpiredDialog] = useState<{ open: boolean }>({ open: false });
  const [linkedinAccountNotFoundDialog, setLinkedinAccountNotFoundDialog] = useState<{ open: boolean }>({ open: false });

  const [stopRunningCampaignDialog, setStopRunningCampaignDialog] = useState<{
    open: boolean;
    loading: boolean;
    campaignBuilderStep: CampaignBuilderSteps | null;
  }>({
    open: false,
    loading: false,
    campaignBuilderStep: null,
  });

  const [startingCampaignNotPossibleDialog, setStartingCampaignNotPossibleDialog] = useState<{ open: boolean; issues: string[] }>({
    open: false,
    issues: [],
  });

  const [unableToGenerateMessageDialog, setUnableToGenerateMessageDialog] = useState<{ open: boolean }>({ open: false });

  // Info/FAQ dialogs
  const [faqDialog, setFaqDialog] = useState<{ open: boolean; activeFaq: FaqItemKey }>({
    open: false,
    activeFaq: FaqItemKey.SYNCHRONIZING_YOUR_ACCOUNT,
  });

  // Reset functions
  const resetCreateCampaignDialog = useCallback(() => {
    setCreateCampaignDialog({
      open: false,
      loading: false,
      nameTakenError: false,
      name: '',
      selectedLinkedinAccount: '',
      startNewOrClone: StartNewOrClone.START_NEW,
      selectedCampaignToClone: '',
      cloneContacts: false,
    });
  }, []);

  const resetRefineRecruiterSearchDialog = useCallback(() => {
    setRefineRecruiterSearchDialog({
      open: false,
      loading: false,
      crawlerLinkedinAccount: null,
      preLoadingFields: false,
      contactsLoading: false,
      constraints: initialRecruiterSearchConstraints,
      contacts: [],
      totalContacts: 0,
      pageNumber: 1,
      searchRequestId: undefined,
      searchHistoryId: undefined,
      searchContextId: undefined,
    });
  }, []);

  return {
    // Campaign creation
    createCampaignDialog,
    setCreateCampaignDialog,
    resetCreateCampaignDialog,
    createCampaignSuccessDialog,
    setCreateCampaignSuccessDialog,
    openTemplateSuccessDialog,
    setOpenTemplateSuccessDialog,

    // Search dialogs
    refineLinkedinSearchDialog,
    setRefineLinkedinSearchDialog,
    refineRecruiterSearchDialog,
    setRefineRecruiterSearchDialog,
    resetRefineRecruiterSearchDialog,
    createNewSearchDialog,
    setCreateNewSearchDialog,
    addMoreSearchesDialog,
    setAddMoreSearchesDialog,

    // Qualification
    qualifyContactsDialog,
    setQualifyContactsDialog,

    // Contact management
    loadContactsNumberDialog,
    setLoadContactsNumberDialog,
    fileUploadDialog,
    setFileUploadDialog,
    renameContactSearchDialog,
    setRenameContactSearchDialog,
    editSearchLinkedinAccountDialog,
    setEditSearchLinkedinAccountDialog,
    deleteContactsSearchDialog,
    setDeleteContactsSearchDialog,
    deleteContactsFileDialog,
    setDeleteContactsFileDialog,
    addSingleContactDialog,
    setAddSingleContactDialog,
    deleteContactPermanentlyDialog,
    setDeleteContactPermanentlyDialog,
    changeAiJudgementDialog,
    setChangeAiJudgementDialog,
    deleteMultipleContactsPermanentlyDialog,
    setDeleteMultipleContactsPermanentlyDialog,

    // Blacklist dialogs
    addLocalBlacklistDialog,
    setAddLocalBlacklistDialog,
    deleteBlacklistDialog,
    setDeleteBlacklistDialog,
    editBlacklistNameDialog,
    setEditBlacklistNameDialog,

    // Enrichment
    enrichContactsDialog,
    setEnrichContactsDialog,
    enrichmentLoadingDialog,
    setEnrichmentLoadingDialog,

    // Flow creation
    flowCreationDialog,
    setFlowCreationDialog,
    changeCommunicationStyleWarningDialog,
    setChangeCommunicationStyleWarningDialog,

    // Campaign start/stop
    startCampaignSuccessDialog,
    setStartCampaignSuccessDialog,
    startMessageApprovalCampaignSuccessDialog,
    setStartMessageApprovalCampaignSuccessDialog,
    startImmediatelyDialog,
    setStartImmediatelyDialog,
    startImmediatelySuccessDialog,
    setStartImmediatelySuccessDialog,
    flowIncompleteDialog,
    setFlowIncompleteDialog,
    flowEmptyDialog,
    setFlowEmptyDialog,
    creditsExhaustedDialog,
    setCreditsExhaustedDialog,
    copilotLicenseExpiredDialog,
    setCopilotLicenseExpiredDialog,
    linkedinAccountNotFoundDialog,
    setLinkedinAccountNotFoundDialog,
    stopRunningCampaignDialog,
    setStopRunningCampaignDialog,
    startingCampaignNotPossibleDialog,
    setStartingCampaignNotPossibleDialog,
    unableToGenerateMessageDialog,
    setUnableToGenerateMessageDialog,

    // Info/FAQ
    faqDialog,
    setFaqDialog,

    // Constants
    initialRecruiterSearchConstraints,
    initialQualificationPromptPlaceholders,
  };
}

